/**
 * 文件 URL 工具 — 规范 /api/v1/ 路径与遗留路径归一化（simplify-nginx-routing Phase D）
 */

import { getCorrectedApiBaseUrl } from '@/shared/api/api-base'

/** 与 backend url_spec.PATH_SEGMENTS 一致 */
const PATH_SEGMENTS = ['temp', 'document', 'report', 'video', 'subtitle', 'audio'] as const

const DEPRECATED_DOCUMENT_ROOT = '/document/'
const DEPRECATED_UPLOADS_ROOT = '/uploads/'
const DEPRECATED_API_V1_UPLOADS_ROOT = '/api/v1/uploads/'
const API_PREFIX = '/api/v1'

const ROOT_FILE_PATH_RE = new RegExp(
  `^/(?:${PATH_SEGMENTS.join('|')})/`
)

function joinApiPath(...parts: string[]): string {
  const segments: string[] = []
  for (const part of parts) {
    for (const segment of part.split('/')) {
      if (segment) segments.push(segment)
    }
  }
  return segments.length ? `${API_PREFIX}/${segments.join('/')}` : API_PREFIX
}

/**
 * 将遗留路径归一化为 /api/v1/{file_key}（与 backend FileUrlBuilder.normalize_legacy_url 对齐）
 */
export function normalizeLegacyFileUrl(path: string): string {
  const trimmed = path.trim()
  if (!trimmed) return trimmed

  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`

  if (normalized.startsWith(DEPRECATED_API_V1_UPLOADS_ROOT)) {
    const remainder = normalized.slice(DEPRECATED_API_V1_UPLOADS_ROOT.length)
    return remainder ? joinApiPath(remainder) : API_PREFIX
  }

  if (
    normalized === API_PREFIX ||
    (normalized.startsWith(`${API_PREFIX}/`) &&
      !normalized.startsWith(DEPRECATED_API_V1_UPLOADS_ROOT))
  ) {
    return normalized
  }

  if (normalized.startsWith(DEPRECATED_DOCUMENT_ROOT)) {
    const remainder = normalized.slice(DEPRECATED_DOCUMENT_ROOT.length)
    return remainder ? joinApiPath('document', remainder) : joinApiPath('document')
  }

  if (normalized.startsWith(DEPRECATED_UPLOADS_ROOT)) {
    const remainder = normalized.slice(DEPRECATED_UPLOADS_ROOT.length)
    return remainder ? joinApiPath(remainder) : API_PREFIX
  }

  const stripped = normalized.replace(/^\/+/, '')
  if (stripped) {
    const firstSegment = stripped.split('/')[0]
    if (PATH_SEGMENTS.includes(firstSegment as (typeof PATH_SEGMENTS)[number])) {
      return joinApiPath(stripped)
    }
  }

  return normalized
}

function isOssHttpsUrl(url: string): boolean {
  if (!url.startsWith('https://')) return false
  try {
    return new URL(url).hostname.includes('aliyuncs.com')
  } catch {
    return false
  }
}

/**
 * 将文件路径或 URL 规范为浏览器可访问形式。
 * 规范 /api/v1/ URL 原样返回；遗留根路径可选归一化后加 API 前缀。
 */
export function publicFileAccessUrl(pathOrUrl: string | undefined | null): string {
  const s = (pathOrUrl ?? '').trim()
  if (!s) return ''
  if (s.startsWith('//')) return s

  if (s.startsWith('http://') || s.startsWith('https://')) {
    if (isOssHttpsUrl(s)) return s
    try {
      const u = new URL(s)
      if (u.pathname.startsWith(`${API_PREFIX}/`) || u.pathname === API_PREFIX) return s
      const normalizedPath = normalizeLegacyFileUrl(u.pathname || '/')
      if (
        typeof window !== 'undefined' &&
        u.origin === window.location.origin &&
        normalizedPath !== u.pathname
      ) {
        const base = getCorrectedApiBaseUrl().replace(/\/$/, '')
        return `${base}${normalizedPath}${u.search}`
      }
    } catch {
      return s
    }
    return s
  }

  if (s.startsWith(`${API_PREFIX}/`) || s === API_PREFIX) return s

  const legacyNormalized = normalizeLegacyFileUrl(s)
  if (legacyNormalized.startsWith(`${API_PREFIX}/`) || legacyNormalized === API_PREFIX) {
    return legacyNormalized
  }

  if (s.startsWith('/') && ROOT_FILE_PATH_RE.test(s)) {
    const base = getCorrectedApiBaseUrl().replace(/\/$/, '')
    return `${base}${s}`
  }

  return s
}

/** 按文件 ID 生成预览 proxy URL：/api/v1/files/{id}/proxy */
export function fileProxyUrl(fileId: number, baseUrl?: string): string {
  const base = (baseUrl ?? getCorrectedApiBaseUrl()).replace(/\/$/, '')
  return `${base}/files/${fileId}/proxy`
}
